import {
  HasuraDataTableColumnDef,
  HasuraDataTableCustomFilter,
  DataTableEx,
  DataTableExProps,
  DataTableExRef,
} from '@kirz/mui-admin';
import { IconButton, Stack, Tooltip } from '@mui/material';
import { Download, Eye } from 'mdi-material-ui';
import React, {
  forwardRef,
  Ref,
  useImperativeHandle,
  useMemo,
  useRef,
} from 'react';

import { XmlAttachmentsButton } from './XmlAttachmentsButton';

/**
 * Custom filters are addressed by their position in the `customFilter.filters`
 * array (mui-admin keys the persisted state as `<filterIndex>_<inputIndex>`),
 * so the order below is part of the URL contract — see `logsFilterUrl`.
 */
export const LOGS_FILTER_INDEX = {
  client: 0,
  company: 1,
  folder: 2,
  from: 3,
  to: 4,
};

/**
 * Builds a link to the logs page with pre-applied filters, e.g.
 * `logsFilterUrl({ client: [42] })` -> `/logs?f=%7B%220_0%22%3A%5B42%5D%7D`
 */
export function logsFilterUrl(
  filters: Partial<Record<keyof typeof LOGS_FILTER_INDEX, any>>,
) {
  const state = Object.fromEntries(
    Object.entries(filters).map(([key, value]) => [
      `${LOGS_FILTER_INDEX[key as keyof typeof LOGS_FILTER_INDEX]}_0`,
      value,
    ]),
  );

  return `/logs?f=${encodeURIComponent(JSON.stringify(state))}`;
}

export const LogsTable = forwardRef(
  (
    props: Partial<DataTableExProps> & {
      id: string;
      filterColumns?: (
        columns: HasuraDataTableColumnDef[],
      ) => HasuraDataTableColumnDef[];
    },
    ref: Ref<DataTableExRef>,
  ) => {
    const { filterColumns, ...rest } = props;

    const tableRef = useRef<DataTableExRef>(null);

    useImperativeHandle(ref, () => tableRef.current!, []);

    const columns = useMemo<HasuraDataTableColumnDef[]>(
      () =>
        (filterColumns || ((x: HasuraDataTableColumnDef[]) => x))([
          {
            field: 'id',
            headerName: 'ID',
            width: 80,
          },
          {
            field: 'text',
            headerName: 'Tekst',
            flex: 1,
            minWidth: 240,
          },
          {
            field: 'clientName',
            headerName: 'Klant',
            selector: 'client { id firstName lastName }',
            flex: 1,
            minWidth: 160,
            valueGetter({ row }) {
              return row.client
                ? `${row.client.firstName ?? ''} ${
                    row.client.lastName ?? ''
                  }`.trim()
                : '';
            },
            onSort(sort) {
              return [{ client: { firstName: sort } }];
            },
          },
          {
            field: 'companyName',
            headerName: 'Bedrijf',
            selector: 'company { id name bkh }',
            flex: 1,
            minWidth: 160,
            valueGetter({ row }) {
              return row.company?.name ?? '';
            },
            onSort(sort) {
              return [{ company: { name: sort } }];
            },
          },
          {
            field: 'folder',
            headerName: 'Map',
            width: 150,
          },
          {
            field: 'document',
            headerName: 'Document',
            selector: 'file { id publicId name extension isRemoved }',
            sortable: false,
            width: 150,
            renderCell({ row }) {
              const { file } = row;

              if (!file || file.isRemoved) {
                return null;
              }

              const fileName = `${file.name}${file.extension}`;

              return (
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <Tooltip title={`Open ${fileName}`}>
                    <IconButton
                      component="a"
                      href={`/api/v1/files/${file.publicId}`}
                      target="_blank"
                      size="small"
                    >
                      <Eye fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <XmlAttachmentsButton file={file} />
                  <Tooltip title={`Download ${fileName}`}>
                    <IconButton
                      component="a"
                      href={`/api/v1/files/${file.publicId}/${fileName}`}
                      target="_blank"
                      size="small"
                      download
                    >
                      <Download fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
              );
            },
          },
          {
            field: 'createdAt',
            headerName: 'Aangemaakt op',
            type: 'date',
            width: 200,
          },
        ]),
      [filterColumns],
    );

    const filters = useMemo<HasuraDataTableCustomFilter[]>(
      () => [
        {
          type: 'autocomplete',
          mode: 'hasura',
          name: 'Klant',
          field: 'clientId',
          source: 'client',
          selection: 'id firstName lastName',
          itemText: (item) =>
            `${item.firstName ?? ''} ${item.lastName ?? ''}`.trim(),
          multiple: true,
          filter: (value) =>
            value?.length ? { clientId: { _in: value } } : {},
        },
        {
          type: 'autocomplete',
          mode: 'hasura',
          name: 'Bedrijf',
          field: 'companyId',
          source: 'company',
          selection: 'id name bkh',
          itemText: (item) =>
            `${item.name ?? ''}${item.bkh ? ` [#${item.bkh}]` : ''}`.trim(),
          multiple: true,
          filter: (value) =>
            value?.length ? { companyId: { _in: value } } : {},
        },
        {
          type: 'autocomplete',
          mode: 'hasura',
          name: 'Map',
          field: 'folder',
          source: 'directory',
          selection: 'id name',
          itemText: 'name',
          itemValue: 'name',
          multiple: true,
          filter: (value) => (value?.length ? { folder: { _in: value } } : {}),
        },
        {
          type: 'date',
          name: 'Datum (van)',
          field: 'from',
          filter: (value) => (value ? { createdAt: { _gte: value } } : {}),
        },
        {
          type: 'date',
          name: 'Datum (tot)',
          field: 'to',
          filter: (value) => (value ? { createdAt: { _lte: value } } : {}),
        },
      ],
      [],
    );

    return (
      <DataTableEx
        {...rest}
        ref={tableRef}
        source="appLog"
        columns={columns}
        persistStateMode="query"
        sortBy={rest.sortBy ?? { field: 'createdAt', sort: 'desc' }}
        customFilter={rest.customFilter ?? { filters }}
        searchFilter={
          rest.searchFilter ?? {
            inputProps: {
              placeholder: 'Zoeken op klant, bedrijf, map of tekst',
            },
            filter: (search: string[]) => ({
              _or: search.flatMap((str) => [
                { text: { _ilike: `%${str}%` } },
                { folder: { _ilike: `%${str}%` } },
                { client: { firstName: { _ilike: `%${str}%` } } },
                { client: { lastName: { _ilike: `%${str}%` } } },
                { company: { name: { _ilike: `%${str}%` } } },
                { company: { bkh: { _ilike: `%${str}%` } } },
              ]),
            }),
          }
        }
      />
    );
  },
);
